import { useFlags } from 'launchdarkly-react-client-sdk';

import { useUserContext } from 'contexts/UserContext/UserContextProvider';
import { useTeamPermissionsQuery } from 'queries/permissions';

interface User {
  id: string;
}

interface TeamMembership {
  user: User;
  permissions: string[];
}

interface Team {
  id: string;
  restrict_signals_resource_management: boolean;
  memberships: TeamMembership[];
}

interface UseAuthorizationV2Response {
  isAuthorized: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  displayName?: string;
}

/**
 * Hook to handle authorization logic based on user permissions.
 *
 * @param {string} required_permission - The permission required.
 * @param {Team} team - The team to check permissions for.
 * @param {boolean} signalsResource - Whether the team restricts signals resource management.
 * @returns {Object} An object containing:
 *   - `isAuthorized` {boolean}: Indicates whether the user is authorized.
 *   - `isLoading` {boolean}: Indicates whether the permissions are still loading.
 *   - `isError` {boolean}: Indicates whether there was an error while fetching permissions.
 *   - `error` {Error|undefined}: The error object if an error occurred, otherwise undefined.
 *
 * @example
 * const { isAuthorized, isLoading, isError, error } = useAuthorizationV2('view_dashboard');
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * if (isError) {
 *   return <ErrorMessage error={error} />;
 * }
 * if (isAuthorized) {
 *   return <Dashboard />;
 * }
 * return <UnauthorizedMessage />;
 */
export const useTeamAuthorizationV2 = (
  requiredPermission: string,
  team: Team | null,
  signalsResource: boolean,
): UseAuthorizationV2Response => {
  const { rbacCandidateControl } = useFlags();

  const { currentUser, isUserOwner } = useUserContext();

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isError,
    error: permissionsError,
  } = useTeamPermissionsQuery();

  // If no team is provided, return unauthorized
  if (!team) {
    return {
      isAuthorized: false,
      isLoading: false,
      isError: false,
      displayName: undefined,
    } as UseAuthorizationV2Response;
  }

  const userPermissions =
    team?.memberships?.find((member) => member?.user?.id === currentUser?.id)
      ?.permissions || [];

  if (!rbacCandidateControl) {
    return {
      isAuthorized: true,
      isLoading: false,
    } as UseAuthorizationV2Response;
  }

  if (!requiredPermission) {
    return {
      isAuthorized: true,
      isLoading: false,
    } as UseAuthorizationV2Response;
  }

  if (
    (!team?.restrict_signals_resource_management && signalsResource) ||
    isUserOwner
  ) {
    return {
      isAuthorized: true,
      isLoading: false,
    } as UseAuthorizationV2Response;
  }

  if (isPermissionsLoading) {
    return {
      isAuthorized: false,
      isLoading: true,
    } as UseAuthorizationV2Response;
  }

  if (isError) {
    return {
      isAuthorized: false,
      isLoading: false,
      isError: isError,
      error: permissionsError,
    } as UseAuthorizationV2Response;
  }

  const displayName = permissions?.find(
    (permission) => permission.slug === requiredPermission,
  )?.display_name;

  if (!userPermissions || userPermissions.length === 0) {
    return {
      isAuthorized: false,
      isLoading: false,
      displayName: displayName,
    } as UseAuthorizationV2Response;
  }

  const hasPermission = userPermissions.some(
    (permission) => permission === requiredPermission,
  );

  return {
    isAuthorized: hasPermission,
    isLoading: false,
    displayName: displayName,
  } as UseAuthorizationV2Response;
};
